import { IdbService } from '../IdbService.js';
import { getAllFromObjectStores, getSizeOfObjectStore } from '../util.js';
export class IdbPersistenceFileDb extends IdbService {
    constructor() {
        super(...arguments);
        this.add = async (file) => {
            let buffer = file.file ? await fileToArrayBuffer(file.file) : undefined;
            await this.run('files', (store) => {
                return store.put({
                    id: file.id,
                    // IDB doesn't support boolean indexes
                    remote: file.remote ? 'true' : 'false',
                    deletedAt: null,
                    name: file.name,
                    type: file.type,
                    url: file.url,
                    buffer,
                    timestamp: file.timestamp,
                });
            }, {
                mode: 'readwrite',
            });
        };
        this.markUploaded = async (id) => {
            if (this.disposed) {
                return;
            }
            const current = await this.getFileRaw(id);
            if (!current) {
                throw new Error('File is not in local database');
            }
            await this.run('files', (store) => {
                return store.put(Object.assign(Object.assign({}, current), { remote: 'true' }));
            }, {
                mode: 'readwrite',
            });
        };
        this.get = async (fileId) => {
            const raw = await this.getFileRaw(fileId);
            if (!raw) {
                return null;
            }
            return this.hydrateFileData(raw);
        };
        this.delete = (fileId) => {
            return this.run('files', (store) => {
                return store.delete(fileId);
            }, {
                mode: 'readwrite',
            });
        };
        this.markPendingDelete = async (fileId) => {
            const current = await this.getFileRaw(fileId);
            if (!current) {
                throw new Error('File is not in local database');
            }
            await this.run('files', (store) => {
                return store.put(Object.assign(Object.assign({}, current), { deletedAt: Date.now() }));
            }, {
                mode: 'readwrite',
            });
        };
        this.listUnsynced = async () => {
            const raw = await this.run('files', (store) => {
                return store.index('remote').getAll('false');
            }, { mode: 'readonly' });
            return raw.map(this.hydrateFileData);
        };
        this.resetSyncedStatusSince = async (since) => {
            const tx = this.createTransaction(['files'], {
                mode: 'readwrite',
            });
            const raw = await this.run('files', (store) => {
                return store.index('remote').getAll('true');
            }, { transaction: tx });
            const filtered = raw.filter((file) => !file.timestamp || !since || file.timestamp > since);
            await Promise.all(filtered.map((file) => {
                return this.run('files', (store) => {
                    return store.put(Object.assign(Object.assign({}, file), { remote: 'false' }));
                }, { transaction: tx });
            }));
        };
        this.iterateOverPendingDelete = (iterator) => {
            return this.iterate('files', (store) => {
                return store
                    .index('deletedAt')
                    .openCursor(IDBKeyRange.lowerBound(0, true));
            }, (value, store) => {
                iterator(this.hydrateFileData(value), store);
            }, {
                mode: 'readwrite',
            });
        };
        this.getAll = async (options) => {
            const [files] = await getAllFromObjectStores(this.db, ['files']);
            return files.map(this.hydrateFileData);
        };
        this.stats = async () => {
            return {
                size: await getSizeOfObjectStore(this.db, 'files'),
            };
        };
        this.loadFileContents = async (file, ctx) => {
            if (file.file)
                return file.file;
            if (file.localPath) {
                throw new Error('Local file paths are not supported in browser');
            }
            if (file.url) {
                const response = await ctx.environment.fetch(file.url);
                if (!response.ok) {
                    throw new Error(`Failed to download file ${file.url}: ${response.statusText}`);
                }
                return response.blob();
            }
            throw new Error('File is missing url, file, and localPath');
        };
        this.hydrateFileData = (raw) => {
            raw.remote = raw.remote === 'true';
            const buffer = raw.buffer;
            delete raw.buffer;
            raw.file = buffer
                ? arrayBufferToBlob(buffer, raw.type, raw.name)
                : undefined;
            return raw;
        };
        this.getFileRaw = async (id, { transaction } = {}) => {
            if (this.disposed) {
                return undefined;
            }
            const raw = await this.run('files', (store) => {
                return store.get(id);
            }, { mode: 'readonly', transaction: transaction });
            if (!raw) {
                return undefined;
            }
            return raw;
        };
    }
}
export function arrayBufferToBlob(buffer, type, name) {
    return new File([new Blob([buffer], { type })], name !== null && name !== void 0 ? name : 'blob', {
        type,
    });
}
function fileToArrayBuffer(file) {
    // special case for testing...
    if ('__testReadBuffer' in file) {
        return Promise.resolve(file.__testReadBuffer);
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
}
//# sourceMappingURL=IdbPersistenceFileDb.js.map